import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Warning, ArrowsClockwise, XCircle, CaretDown, CaretUp, Info,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';


const STATUT_CFG = {
  EN_PREPARATION: { bg: 'bg-[#F5F5F3]', text: 'text-[#6B6B6B]', label: 'En préparation' },
  EN_COURS: { bg: 'bg-[#E3F2FD]', text: 'text-[#1E3A5F]', label: 'En cours' },
  ATTRIBUEE: { bg: 'bg-[#E8F5E9]', text: 'text-[#2D6A4F]', label: 'Attribué' },
  ANNULE: { bg: 'bg-[#FFEBEE]', text: 'text-[#8B3A3A]', label: 'Annulé' },
};


const DpgfDceConsistencyBanner = forwardRef(({ chantierId, onActionComplete }, ref) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(null);
  const [cancelMotif, setCancelMotif] = useState('');
  const [syncing, setSyncing] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!chantierId) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/dpgf-dce-consistency`,
        { withCredentials: true, silent403: true },  // A6 — pas de message rouge pour la collaboratrice
      );
      setData(data);
    } catch (e) {
      // 403 / 404 silencieux (pas tous les rôles voient ce bandeau)
      console.debug('[ConsistencyBanner] load skipped:', e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  // Expose reload() to parents via ref
  useImperativeHandle(ref, () => ({ reload: load }), [load]);

  if (loading || !data || !data.has_divergences) return null;

  const dcesWithDivergences = data.dces.filter(d =>
    d.lots_added.length > 0 || d.lots_removed.length > 0 || d.lots_modified.length > 0,
  );
  if (dcesWithDivergences.length === 0) return null;

  const handleSync = async (dceId) => {
    if (!window.confirm(
      'Resynchroniser le snapshot DCE avec le DPGF actuel ?\n\n'
      + 'Le DCE reflétera la nouvelle structure. Action irréversible.',
    )) return;
    setSyncing(dceId);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/sync-snapshot`,
        {}, { withCredentials: true },
      );
      toast.success('Snapshot resynchronisé avec le DPGF actuel');
      await load();
      onActionComplete?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sync');
    } finally {
      setSyncing(null);
    }
  };

  const handleCancel = async () => {
    if (cancelMotif.length < 10) {
      toast.error('Motif requis (minimum 10 caractères)');
      return;
    }
    setCancelling(true);
    try {
      const { data: r } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${showCancelDialog}/cancel`,
        { motif: cancelMotif, confirm: 'YES_CANCEL' },
        { withCredentials: true },
      );
      toast.success(`DCE annulé · ${r.notifications_envoyees} entreprise(s) notifiée(s)`);
      setShowCancelDialog(null);
      setCancelMotif('');
      await load();
      onActionComplete?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur annulation');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <div
        data-testid="consistency-banner"
        className="bg-[#FFF8E1] border-l-4 border-[#B8860B] mb-4"
      >
        <div className="px-4 py-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Warning size={22} className="text-[#B8860B] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#5A4A0B]" data-testid="consistency-banner-title">
                Divergence DPGF ↔ DCE détectée
              </p>
              <p className="text-sm text-[#6B5A1F] mt-1">
                Le DPGF actuel diffère du contenu de{' '}
                <strong>{dcesWithDivergences.length} DCE actif{dcesWithDivergences.length > 1 ? 's' : ''}</strong>.
                Les entreprises invitées voient le contenu original tel qu'à l'envoi (engagement contractuel).
              </p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-bold text-[#B8860B] hover:text-[#5A4A0B] flex items-center gap-1 px-2 py-1 shrink-0"
            data-testid="btn-consistency-toggle"
          >
            {expanded ? <><CaretUp size={14} /> Masquer</> : <><CaretDown size={14} /> Détails</>}
          </button>
        </div>

        {expanded && (
          <div className="px-4 pb-4 border-t border-[#E8D87F] mt-2 space-y-3">
            {dcesWithDivergences.map(dce => (
              <DceCard
                key={dce.dce_id}
                dce={dce}
                onSync={() => handleSync(dce.dce_id)}
                onCancel={() => setShowCancelDialog(dce.dce_id)}
                isSyncing={syncing === dce.dce_id}
              />
            ))}
          </div>
        )}
      </div>

      {showCancelDialog && (
        <CancelDceDialog
          motif={cancelMotif}
          setMotif={setCancelMotif}
          cancelling={cancelling}
          onClose={() => { setShowCancelDialog(null); setCancelMotif(''); }}
          onConfirm={handleCancel}
        />
      )}
    </>
  );
});

DpgfDceConsistencyBanner.displayName = 'DpgfDceConsistencyBanner';

export default DpgfDceConsistencyBanner;


function DceCard({ dce, onSync, onCancel, isSyncing }) {
  const cfg = STATUT_CFG[dce.statut] || STATUT_CFG.EN_PREPARATION;
  const canSync = dce.actions_possibles.includes('sync_snapshot');
  const canCancel = dce.actions_possibles.includes('cancel_dce');
  const isBlocked = dce.actions_possibles.includes('blocked_attributed');
  const needsSprintD = dce.actions_possibles.includes('modify_via_sprint_d');

  return (
    <div
      className="border border-[#E0E0DB] bg-white p-3 space-y-2"
      data-testid={`dce-card-${dce.dce_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 font-bold ${cfg.bg} ${cfg.text}`}>
          DCE {cfg.label}
        </span>
        <span className="text-xs text-[#9A9A9A] font-mono">{dce.dce_id.slice(0, 8)}…</span>
        {dce.date_lancement && (
          <span className="text-xs text-[#9A9A9A]">
            lancé le {new Date(dce.date_lancement).toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>

      {/* Résumé divergences */}
      <div className="text-sm space-y-1">
        {dce.lots_added.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-[#2D6A4F] font-bold text-xs px-1.5 py-0.5 bg-[#E8F5E9] shrink-0">
              +{dce.lots_added.length}
            </span>
            <span className="text-[#2C2C2C]">
              Ajoutés au DPGF actuel : {dce.lots_added.map(l => l.code || l.nom).join(', ')}
            </span>
          </div>
        )}
        {dce.lots_removed.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-[#8B3A3A] font-bold text-xs px-1.5 py-0.5 bg-[#FFEBEE] shrink-0">
              −{dce.lots_removed.length}
            </span>
            <span className="text-[#2C2C2C]">
              Supprimés du DPGF (toujours dans le DCE) : {dce.lots_removed.map(l => l.code || l.nom).join(', ')}
            </span>
          </div>
        )}
        {dce.lots_modified.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-[#B8860B] font-bold text-xs px-1.5 py-0.5 bg-[#FFF8E1] shrink-0">
              ~{dce.lots_modified.length}
            </span>
            <span className="text-[#2C2C2C]">
              Modifiés : {dce.lots_modified.map(l => l.code || l.nom).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Entreprises impactées */}
      <div className="text-xs text-[#6B6B6B] border-t border-[#E0E0DB] pt-2">
        <span className="font-medium">{dce.entreprises_impactees.length} entreprise(s) invitée(s)</span>
        {dce.entreprises_impactees.filter(e => e.a_repondu).length > 0 && (
          <span className="text-[#1E3A5F] ml-1">
            · {dce.entreprises_impactees.filter(e => e.a_repondu).length} ayant répondu
          </span>
        )}
      </div>

      {/* Lots attribués */}
      {dce.lots_attribues_count > 0 && (
        <div className="text-xs bg-[#E8F5E9] p-2 border border-[#C8E6C9]">
          <p className="font-bold text-[#2D6A4F] mb-1">⚠ {dce.lots_attribues_count} lot(s) déjà attribué(s)</p>
          {dce.lots_attribues_details.map((a, i) => (
            <div key={i} className="text-[#1B5E20]">
              • {a.lot_code} → {a.entreprise_nom}
              {a.montant > 0 && <span> ({a.montant.toLocaleString('fr-FR')} € HT)</span>}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t border-[#E0E0DB] flex-wrap">
        {isBlocked && (
          <div className="flex items-start gap-2 text-xs text-[#8B3A3A] bg-[#FFEBEE] p-2 flex-1">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Lots déjà attribués via ce DCE → utilisation des <strong>avenants obligatoire</strong>.
              Toute modification du DPGF doit passer par <code>DPGFAvenantsManager</code>.
            </span>
          </div>
        )}

        {needsSprintD && !isBlocked && (
          <div className="flex items-start gap-2 text-xs text-[#1E3A5F] bg-[#E3F2FD] p-2 flex-1">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Une entreprise a déjà répondu → sync impossible. Utilisez les
              <strong> modifications DPGF in-flight (Sprint D)</strong> pour des modifs ciblées avec notification entreprise.
            </span>
          </div>
        )}

        {canSync && (
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#1E3A5F] bg-[#E3F2FD] text-[#1E3A5F] hover:bg-[#BBDEFB] disabled:opacity-50 transition-colors font-bold"
            data-testid={`btn-sync-${dce.dce_id}`}
          >
            <ArrowsClockwise size={14} />
            {isSyncing ? 'Synchronisation…' : 'Sync snapshot avec DPGF'}
          </button>
        )}

        {canCancel && (
          <button
            onClick={onCancel}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#8B3A3A] bg-[#FFEBEE] text-[#8B3A3A] hover:bg-[#FFCDD2] transition-colors font-bold"
            data-testid={`btn-cancel-${dce.dce_id}`}
          >
            <XCircle size={14} />
            Annuler ce DCE
          </button>
        )}
      </div>
    </div>
  );
}


function CancelDceDialog({ motif, setMotif, cancelling, onClose, onConfirm }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="cancel-dce-dialog"
    >
      <div className="bg-white max-w-xl w-full p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-[#8B3A3A] mb-1">Annuler ce DCE ?</h2>
          <p className="text-sm text-[#2C2C2C]">
            Cette action <strong>annule définitivement</strong> le DCE.
            Les entreprises invitées seront <strong>notifiées par email</strong> avec le motif que vous saisissez.
          </p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider font-bold block mb-1 text-[#6B6B6B]">
            Motif d'annulation (communiqué aux entreprises)
          </label>
          <textarea
            value={motif}
            onChange={e => setMotif(e.target.value)}
            rows={4}
            maxLength={500}
            className="w-full border border-[#E0E0DB] p-2 text-sm focus:outline-none focus:border-[#8B3A3A]"
            placeholder="Ex: Modification importante du programme suite à demande du maître d'ouvrage. La consultation sera relancée sur des bases revues."
            data-testid="input-cancel-motif"
          />
          <p className="text-xs text-[#9A9A9A] mt-1">
            {motif.length} / 500 caractères · minimum 10
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#E0E0DB]">
          <button
            onClick={onClose}
            disabled={cancelling}
            className="px-3 py-1.5 border border-[#E0E0DB] text-sm hover:bg-[#F5F5F3] disabled:opacity-50"
            data-testid="btn-cancel-dialog-close"
          >
            Garder ce DCE
          </button>
          <button
            onClick={onConfirm}
            disabled={motif.length < 10 || cancelling}
            className="px-4 py-1.5 bg-[#8B3A3A] text-white text-sm font-bold hover:bg-[#6B2A2A] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="btn-cancel-dialog-confirm"
          >
            {cancelling ? 'Annulation…' : 'Annuler le DCE et notifier'}
          </button>
        </div>
      </div>
    </div>
  );
}
